import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Search, ChevronDown, ChevronUp, Download, Check, ExternalLink, Link2 } from 'lucide-react';
import axios from 'axios';
import './Results.css';

const Results = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Фильтры
  const [selectedNeuralNetworks, setSelectedNeuralNetworks] = useState(['all']);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('selectedProject');
    return saved || 'all';
  });
  const [mentionFilter, setMentionFilter] = useState('all'); // all, has_mention, no_mention
  const [sentimentFilter, setSentimentFilter] = useState('all'); // all, positive, neutral, negative

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Выбор строк для экспорта
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedSources, setExpandedSources] = useState(new Set());

  // Загрузка истории проверок
  const fetchHistory = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await axios.get('/api/check-history', {
        params: {
          limit: 1000,
          include_serp: 1,
          project_id: selectedProject,
          search_engine: selectedNeuralNetworks.includes('all') ? undefined : 
            (selectedNeuralNetworks.includes('yandex') && selectedNeuralNetworks.includes('google') ? undefined :
             selectedNeuralNetworks.includes('yandex') ? 'yandex' : 'google')
        }
      });

      const data = response.data.data || response.data;
      if (Array.isArray(data)) {
        const completedOnly = data.filter(item => item.status === 'completed');
        setHistory(completedOnly);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [selectedProject, selectedNeuralNetworks]);

  // Загрузка проектов
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchHistory();
      fetchProjects();
    }
  }, [user, fetchHistory, fetchProjects]);

  // Определение тональности на основе brand_score (бинарное: 1=найден, 0=не найден)
  const getSentiment = (brandScore) => {
    if (brandScore === null || brandScore === undefined) return 'unknown';
    if (brandScore === 0) return 'no_mention';
    return 'positive'; // brand_score = 1 means found
  };

  // Получение названия нейросети
  const getNeuralNetworkName = (searchEngine) => {
    return searchEngine === 'yandex' ? 'Яндекс Алиса' : 'Google AI';
  };

  // Трансформация данных в формат таблицы
  const transformedData = useMemo(() => {
    return history.map(check => {
      let sources = [];
      try {
        if (check.serp_items) {
          const serpData = typeof check.serp_items === 'string'
            ? JSON.parse(check.serp_items)
            : check.serp_items;
          sources = serpData.sources || [];
        }
      } catch (e) {
        console.error('Error parsing sources:', e);
      }

      const brandScore = check.brand_score;
      const hasMention = brandScore !== null && brandScore !== undefined && brandScore > 0;
      const sentiment = getSentiment(brandScore);

      return {
        id: check.id,
        prompt: check.query,
        neuralNetwork: getNeuralNetworkName(check.search_engine),
        searchEngine: check.search_engine,
        response: check.alice_response || '',
        brandScore: brandScore,
        hasMention: hasMention,
        sentiment: sentiment,
        sources: sources,
        aiComment: check.ai_comment,
        recommendations: check.ai_recommendations,
        projectId: check.project_id,
        projectTitle: check.project_title,
        region: check.region,
        checkDate: check.check_date,
        siteMentioned: check.site_mentioned,
        siteMentionDetails: (() => {
          try {
            if (!check.site_mention_details) return null;
            return typeof check.site_mention_details === 'string'
              ? JSON.parse(check.site_mention_details)
              : check.site_mention_details;
          } catch { return null; }
        })()
      };
    });
  }, [history]);

  // Фильтрация данных
  const filteredData = useMemo(() => {
    return transformedData.filter(item => {
      // Фильтр по нейросетям
      if (!selectedNeuralNetworks.includes('all')) {
        if (selectedNeuralNetworks.includes('yandex') && item.searchEngine !== 'yandex') {
          if (!selectedNeuralNetworks.includes('google') || item.searchEngine !== 'google') {
            return false;
          }
        }
        if (selectedNeuralNetworks.includes('google') && item.searchEngine !== 'google') {
          if (!selectedNeuralNetworks.includes('yandex') || item.searchEngine !== 'yandex') {
            return false;
          }
        }
      }

      // Поиск по промпту
      if (searchQuery && !item.prompt.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Фильтр по проекту
      if (selectedProject !== 'all' && item.projectId?.toString() !== selectedProject) {
        return false;
      }

      // Фильтр по упоминаниям
      if (mentionFilter === 'has_mention' && !item.hasMention) {
        return false;
      }
      if (mentionFilter === 'no_mention' && item.hasMention) {
        return false;
      }

      // Фильтр по тональности
      if (sentimentFilter !== 'all' && item.sentiment !== sentimentFilter) {
        return false;
      }

      return true;
    });
  }, [transformedData, selectedNeuralNetworks, searchQuery, selectedProject, mentionFilter, sentimentFilter]);

  // Пагинация
  const itemsCount = filteredData.length;
  const totalPages = Math.ceil(itemsCount / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Сброс страницы при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedNeuralNetworks, searchQuery, selectedProject, mentionFilter, sentimentFilter]);

  // Обработка выбора нейросети
  const handleNeuralNetworkToggle = (network) => {
    if (network === 'all') {
      setSelectedNeuralNetworks(['all']);
    } else {
      const newSelection = selectedNeuralNetworks.filter(n => n !== 'all');
      if (newSelection.includes(network)) {
        const filtered = newSelection.filter(n => n !== network);
        setSelectedNeuralNetworks(filtered.length === 0 ? ['all'] : filtered);
      } else {
        setSelectedNeuralNetworks([...newSelection, network]);
      }
    }
  };

  // Обработка выбора строк
  const handleRowSelect = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === currentItems.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(currentItems.map(item => item.id)));
    }
  };

  // Обработка раскрытия строк
  const toggleRowExpand = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Обработка раскрытия источников
  const toggleSourceExpand = (id) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSources(newExpanded);
  };

  // Экспорт в Excel
  const handleExport = () => {
    const dataToExport = filteredData.filter(item => selectedRows.has(item.id));

    // Формируем CSV
    const headers = ['Промпт', 'Нейросеть', 'Ответ', 'Бренд', 'Сайт в выдаче', 'Источники', 'Комментарий'];
    const rows = dataToExport.map(item => [
      item.prompt,
      item.neuralNetwork,
      item.response?.replace(/<[^>]*>/g, '').substring(0, 500) || '',
      item.hasMention ? 'Найден' : 'Не найден',
      item.siteMentioned === null ? 'Не проверено' : item.siteMentioned ? 'Найден' : 'Не найден',
      item.sources.map(s => s.url || s.domain).join('; '),
      item.aiComment || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `results_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Получение текста ответа без HTML
  const getCleanResponse = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  return (
    <div className="results-page">
      <div className="results-header">
        <h1>Результаты</h1>
        <div className="header-actions">
          {selectedRows.size > 0 && (
            <>
              <span className="selected-count">{selectedRows.size} элементов выбрано</span>
              <button className="export-btn" onClick={handleExport}>
                <Download size={16} />
                Экспорт (Excel)
              </button>
            </>
          )}
        </div>
      </div>

      {/* Фильтры */}
      <div className="results-filters">
        <div className="neural-networks-filter">
          <label className="filter-label">Нейросети</label>
          <div className="neural-network-checkboxes">
            <label className={`nn-checkbox ${selectedNeuralNetworks.includes('all') ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={selectedNeuralNetworks.includes('all')}
                onChange={() => handleNeuralNetworkToggle('all')}
              />
              <span>Все</span>
            </label>
            <label className={`nn-checkbox ${selectedNeuralNetworks.includes('yandex') ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={selectedNeuralNetworks.includes('yandex')}
                onChange={() => handleNeuralNetworkToggle('yandex')}
              />
              <span>Яндекс Алиса</span>
            </label>
            <label className={`nn-checkbox ${selectedNeuralNetworks.includes('google') ? 'active' : ''}`}>
              <input
                type="checkbox"
                checked={selectedNeuralNetworks.includes('google')}
                onChange={() => handleNeuralNetworkToggle('google')}
              />
              <span>Google AI</span>
            </label>
          </div>
        </div>

        <div className="filter-row">
          <select
            className="filter-select"
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              localStorage.setItem('selectedProject', e.target.value);
            }}
          >
            <option value="all">Выбрать проект...</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={mentionFilter}
            onChange={(e) => setMentionFilter(e.target.value)}
          >
            <option value="all">Упоминание</option>
            <option value="has_mention">Есть упоминание</option>
            <option value="no_mention">Нет упоминаний</option>
          </select>

          <select
            className="filter-select"
            value={sentimentFilter}
            onChange={(e) => setSentimentFilter(e.target.value)}
          >
            <option value="all">Тональность</option>
            <option value="positive">Положительная</option>
            <option value="neutral">Нейтральная</option>
            <option value="negative">Негативная</option>
          </select>

          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Поиск по промпту"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Таблица */}
      <div className="results-content">
        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : itemsCount === 0 ? (
          <div className="empty-state">
            <p>Результаты не найдены</p>
            <p>Попробуйте изменить фильтры</p>
          </div>
        ) : (
          <>
            <div className="results-table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedRows.size === currentItems.length && currentItems.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="col-prompt">Промпт</th>
                    <th className="col-neural-network">Нейросеть</th>
                    <th className="col-response">Ответ</th>
                    <th className="col-sources">Источники</th>
                    <th className="col-mentions">Упоминание и тональность</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item) => {
                    const isExpanded = expandedRows.has(item.id);
                    const isSourceExpanded = expandedSources.has(item.id);
                    const cleanResponse = getCleanResponse(item.response);
                    const responsePreview = cleanResponse.substring(0, 150) + (cleanResponse.length > 150 ? '...' : '');

                    return (
                      <React.Fragment key={item.id}>
                        <tr className={selectedRows.has(item.id) ? 'selected' : ''}>
                          <td className="col-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedRows.has(item.id)}
                              onChange={() => handleRowSelect(item.id)}
                            />
                          </td>
                          <td className="col-prompt" data-label="Промпт">
                            <div className="prompt-text">{item.prompt}</div>
                          </td>
                          <td className="col-neural-network" data-label="Нейросеть">
                            <span className={`nn-badge ${item.searchEngine}`}>
                              {item.neuralNetwork}
                            </span>
                          </td>
                          <td className="col-response" data-label="Ответ">
                            <div className="response-preview">
                              {responsePreview || 'Ответ отсутствует'}
                            </div>
                            <button
                              className="details-btn"
                              onClick={() => toggleRowExpand(item.id)}
                              style={{ marginTop: '8px' }}
                            >
                              Подробнее
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          </td>
                          <td className="col-sources" data-label="Источники">
                            {item.sources.length > 0 ? (
                              <div className="sources-column">
                                <div className="sources-count-badge">
                                  <Link2 size={14} />
                                  <span>{item.sources.length} источник{item.sources.length === 1 ? '' : item.sources.length < 5 ? 'а' : 'ов'}</span>
                                </div>
                                <div className="sources-compact-list">
                                  {(isSourceExpanded ? item.sources : item.sources.slice(0, 3)).map((source, idx) => (
                                    <div key={idx} className="source-item-compact">
                                      <span className="source-num">{idx + 1}</span>
                                      <div className="source-info">
                                        <a
                                          href={source.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="source-link"
                                          title={source.url}
                                        >
                                          {source.title || source.domain || source.url?.replace(/^https?:\/\/(www\.)?/, '').split('/')[0] || `Источник ${idx + 1}`}
                                          <ExternalLink size={12} style={{ marginLeft: '4px', flexShrink: 0 }} />
                                        </a>
                                        <span className="source-url-small">{source.url?.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}</span>
                                      </div>
                                    </div>
                                  ))}
                                  {item.sources.length > 3 && (
                                    <button
                                      className="sources-toggle-btn"
                                      onClick={() => toggleSourceExpand(item.id)}
                                    >
                                      {isSourceExpanded ? 'Свернуть' : `Ещё ${item.sources.length - 3}`}
                                      {isSourceExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="no-sources">Нет источников</span>
                            )}
                          </td>
                          <td className="col-mentions" data-label="Бренд и сайт">
                            <div className="mention-badges">
                              <span className={`mention-badge ${item.hasMention ? 'has-mention' : 'no-mention'}`}>
                                {item.hasMention ? '✓ Бренд найден' : '✗ Бренд не найден'}
                              </span>
                              {item.siteMentioned !== null && item.siteMentioned !== undefined && (
                                <span className={`mention-badge ${item.siteMentioned ? 'site-found' : 'site-not-found'}`} title={
                                  item.siteMentionDetails ? (() => {
                                    const d = item.siteMentionDetails;
                                    const parts = [];
                                    if (d.inResponse) parts.push('в ответе AI');
                                    if (d.inSources) parts.push('в источниках');
                                    if (d.inSerp) parts.push('в SERP');
                                    return `Сайт: ${parts.join(', ')}`;
                                  })() : ''
                                }>
                                  {item.siteMentioned ? '🌐 Сайт найден' : '🌐 Сайт не найден'}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="expanded-row">
                            <td colSpan="6">
                              <div className="expanded-content">
                                <div className="expanded-section">
                                  <h4>Полный ответ:</h4>
                                  <div className="full-response" dangerouslySetInnerHTML={{ __html: item.response || 'Ответ отсутствует' }} />
                                </div>
                                {item.sources.length > 0 && (
                                  <div className="expanded-section">
                                    <h4>Источники ({item.sources.length}):</h4>
                                    <ul className="sources-full-list">
                                      {item.sources.map((source, idx) => (
                                        <li key={idx}>
                                          <a href={source.url} target="_blank" rel="noopener noreferrer">
                                            {source.title || source.url}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {item.aiComment && (
                                  <div className="expanded-section">
                                    <h4>Детали проверки бренда:</h4>
                                    <p>{item.aiComment}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            <div className="pagination">
              <div className="pagination-info">
                <span>Показано {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, itemsCount)} из {itemsCount}</span>
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  Первая
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Назад
                </button>
                <span className="page-number">Страница {currentPage} из {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Вперёд
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages}
                >
                  Последняя
                </button>
              </div>
              <div className="items-per-page">
                <span>На странице:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Results;
